import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import { questionService } from '../../services/questionService';
import '../../assets/css/Research.css';

const ProgramAdminInactiveQuestions = () => {
  const [inactiveQuestions, setInactiveQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [reactivatingId, setReactivatingId] = useState(null);
  const QUESTIONS_PER_PAGE = 15;
  const navigate = useNavigate();

  useEffect(() => {
    loadInactiveQuestions();
  }, []);

  const loadInactiveQuestions = async () => {
    try {
      setLoading(true);
      const response = await questionService.getInactiveQuestions();
      
      if (response && response.questions) {
        setInactiveQuestions(response.questions);
      } else {
        throw new Error('Failed to load inactive questions');
      }
    } catch (error) {
      console.error('Error loading inactive questions:', error);
      setError(error.message || 'Failed to load inactive questions');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (questionId) => {
    try {
      setReactivatingId(questionId);
      setError('');
      
      const response = await questionService.reactivateQuestion(questionId);
      
      if (response.success) {
        setSuccessMessage('Question reactivated successfully');
        setShowSuccess(true);
        
        // Remove the reactivated question from the list
        setInactiveQuestions(prev => prev.filter(q => q.id !== questionId));
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to reactivate question');
      }
    } catch (error) {
      console.error('Error reactivating question:', error);
      setError(error.message || 'Failed to reactivate question');
    } finally {
      setReactivatingId(null);
    }
  };

  // Get unique categories from questions
  const categories = React.useMemo(() => {
    const uniqueCategories = new Set(inactiveQuestions.map(question => question.category));
    return Array.from(uniqueCategories).sort();
  }, [inactiveQuestions]);

  // Filter questions based on search term and category filters
  const filteredQuestions = inactiveQuestions.filter(question => {
    const matchesSearch = question.text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || question.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredQuestions.length / QUESTIONS_PER_PAGE);
  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE
  );

  // Reset to first page if filters/search change and current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredQuestions, totalPages]);

  const getProtocolNames = (question) => {
    if (!question.protocols || question.protocols.length === 0) return 'Not linked';
    return question.protocols.map(p => p.name).join(', ');
  };

  const getQuestionChoices = (question) => {
    if (question.type === 'text') {
      return 'Text Answer';
    }
    if (question.type === 'range') {
      return 'Range (1-100)';
    }
    if (!question.choices || question.choices.length === 0) {
      return '-';
    }
    return question.choices.join(', ');
  };

  return (
    <ResearchLayout pageTitle="Inactive Questions">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowSuccess(false)}
            ></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/program-admin/dashboard">
                    <i className="fas fa-home me-1"></i>
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/program-admin/questions/view-all">
                    <i className="fas fa-question-circle me-1"></i>
                    Questions
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Inactive Questions
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Header with Actions */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6 mb-3 mb-md-0">
            <h2 className="mb-0">
              <i className="fas fa-eye-slash text-warning me-2"></i>
              Inactive Questions
            </h2>
            <p className="text-muted mb-0">
              Questions that have been marked as inactive and are not available for linking to protocols.
            </p>
          </div>
          <div className="col-md-6 text-md-end">
            <Link to="/program-admin/questions/view-all" className="btn btn-outline-secondary me-2">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Active Questions
            </Link>
            <Link to="/program-admin/questions" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Create New Question
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="fas fa-search"></i>
                  </span>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search inactive questions by text..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="row">
          <div className="col-12">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Loading inactive questions...</span>
                </div>
                <p className="mt-2">Loading inactive questions...</p>
              </div>
            ) : filteredQuestions.length > 0 ? (
              <>
                <div className="card border-0 shadow-sm">
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Question Text</th>
                          <th>Type</th>
                          <th>Category</th>
                          <th>Options</th>
                          <th>Protocol</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedQuestions.map(question => (
                          <tr key={question.id}>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                {question.text}
                              </div>
                            </td>
                            <td>
                              {question.type === 'mcq' && (
                                <span className="badge bg-primary">Multiple Choice</span>
                              )}
                              {question.type === 'boolean' && (
                                <span className="badge bg-info">True/False</span>
                              )}
                              {question.type === 'text' && (
                                <span className="badge bg-success">Text</span>
                              )}
                              {question.type === 'range' && (
                                <span className="badge bg-warning">Range</span>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {question.category || 'General'}
                              </span>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                {getQuestionChoices(question)}
                              </div>
                            </td>
                            <td>
                              {question.protocols && question.protocols.length > 0 ? (
                                <span className="badge bg-success">
                                  {getProtocolNames(question)}
                                </span>
                              ) : (
                                <span className="badge bg-secondary">
                                  Not linked
                                </span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(question.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleReactivate(question.id)}
                                disabled={reactivatingId === question.id}
                              >
                                {reactivatingId === question.id ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                    Reactivating...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-eye me-1"></i>
                                    Reactivate
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile Card View */}
                  <div className="d-block d-md-none">
                    {paginatedQuestions.map(question => (
                      <div key={question.id} className="card mb-2 border border-warning">
                        <div className="card-body p-2">
                          <div className="mb-1">
                            <strong>Q:</strong> {question.text}
                          </div>
                          <div className="mb-1">
                            <span className="me-2">
                              <strong>Type:</strong> {question.type === 'mcq' ? 'Multiple Choice' : question.type === 'boolean' ? 'True/False' : question.type === 'range' ? 'Range' : 'Text'}
                            </span>
                            <span>
                              <strong>Category:</strong> {question.category || 'General'}
                            </span>
                          </div>
                          <div className="mb-1">
                            <strong>Options:</strong> {getQuestionChoices(question)}
                          </div>
                          <div className="mb-1">
                            <strong>Protocol:</strong> {question.protocols && question.protocols.length > 0 ? getProtocolNames(question) : 'Not linked'}
                          </div>
                          <div className="mb-1">
                            <strong>Created:</strong> {new Date(question.created_at).toLocaleDateString()}
                          </div>
                          <div className="mt-2">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleReactivate(question.id)}
                              disabled={reactivatingId === question.id}
                            >
                              {reactivatingId === question.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1"></span>
                                  Reactivating...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-eye me-1"></i>
                                  Reactivate
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Pagination Controls */}
                <div className="mt-3 d-flex justify-content-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    showInfo={true}
                    totalItems={filteredQuestions.length}
                    itemsPerPage={QUESTIONS_PER_PAGE}
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-5 bg-light rounded">
                <i className="fas fa-eye-slash fa-3x text-muted mb-3"></i>
                <h4>No inactive questions found</h4>
                <p className="text-muted">
                  {searchTerm || categoryFilter !== 'all' ? 
                    'No inactive questions match your search criteria.' : 
                    'You have no inactive questions. Questions become inactive when you mark them as such from the edit question page.'
                  }
                </p>
                <Link to="/program-admin/questions/view-all" className="btn btn-primary mt-2">
                  <i className="fas fa-arrow-left me-2"></i>
                  View Active Questions
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Question Count Summary */}
        {filteredQuestions.length > 0 && (
          <div className="mt-4 text-center">
            <div className="badge bg-light text-dark p-2">
              Showing {filteredQuestions.length} of {inactiveQuestions.length} inactive questions
              {categoryFilter !== 'all' && (
                <span> in {categoryFilter} category</span>
              )}
              {searchTerm && (
                <span> matching "{searchTerm}"</span>
              )}
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminInactiveQuestions;